import { Dock, PageScroll } from "@Components/Page";
import { gatewayUrl } from "@Constants/gatewayUrl";
import { useUserContext } from "@Hooks/contexts/useUserContext";
import { useTempDocumentPath } from "@Hooks/useTempDocumentPath";
import { styled } from "@mui/material/styles";
import { Button, CheckIcon, DocumentIcon, EmptyState, ErrorIcon, GroupedList, ListRow, Spinner } from "@UI";
import { createConfig } from "@Utils/createConfig";
import axios from "axios";
import React, { useEffect } from "react";
import { useTranslation } from "react-i18next";
const FILE_NAME = "formatted-document.docx";
const FileBox = styled(GroupedList)({
    width: "100%",
    margin: "var(--j-sp-3) 0 0",
    textAlign: "left",
    border: "1px solid var(--j-line)",
    borderRadius: "var(--j-r-md)"
});
export function FormattedDocumentFetcher({ header, styleGroupId, setLastStepState, lastStepState, decrementActiveStep }) {
    const { t } = useTranslation();
    const token = useUserContext((s)=>s?.user?.meta?.access_token.token);
    const getTempDocumentPath = useTempDocumentPath();
    const handleUploadAndDownload = async (id)=>{
        try {
            setLastStepState("loading");
            const path = await getTempDocumentPath();
            const url = `${gatewayUrl}/plugins/word/documentStyles/styleGroup/${id}/formatDocument`;
            const header = createConfig(token);
            const body = {
                path
            };
            const resp = await axios.post(url, body, {
                ...header,
                responseType: "arraybuffer"
            });
            if (resp.status !== 200) throw new Error(`Server error: ${resp.statusText}`);
            const blob = new Blob([
                resp.data
            ], {
                type: "application/vnd.openxmlformats-officedocument.wordprocessingml.document"
            });
            const blobUrl = window.URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = blobUrl;
            a.download = FILE_NAME;
            document.body.appendChild(a);
            a.click();
            a.remove();
            window.URL.revokeObjectURL(blobUrl);
            setLastStepState("done");
        } catch (err) {
            console.error("Error fetching formatted document:", err);
            setLastStepState("error");
        }
    };
    useEffect(()=>{
        handleUploadAndDownload(styleGroupId);
    }, [
        styleGroupId
    ]);
    const isLoading = lastStepState === "loading" || lastStepState === "idle";
    return /*#__PURE__*/ React.createElement(React.Fragment, null, /*#__PURE__*/ React.createElement(PageScroll, null, header, isLoading && /*#__PURE__*/ React.createElement(EmptyState, {
        icon: /*#__PURE__*/ React.createElement(Spinner, {
            size: "lg"
        }),
        text: t("workflows.StyleConverter.formattingDocument")
    }), lastStepState === "done" && /*#__PURE__*/ React.createElement(EmptyState, {
        icon: /*#__PURE__*/ React.createElement(CheckIcon, null),
        tone: "ok",
        size: "lg",
        title: t("workflows.StyleConverter.successfullyFormattedDocument"),
        text: t("workflows.StyleConverter.downloadedHint")
    }, /*#__PURE__*/ React.createElement(FileBox, null, /*#__PURE__*/ React.createElement(ListRow, {
        icon: /*#__PURE__*/ React.createElement(DocumentIcon, null),
        title: FILE_NAME
    }))), lastStepState === "error" && /*#__PURE__*/ React.createElement(EmptyState, {
        icon: /*#__PURE__*/ React.createElement(ErrorIcon, null),
        tone: "warn",
        title: t("workflows.StyleConverter.formatFailed"),
        text: t("workflows.StyleConverter.errorFetchingDocument")
    })), !isLoading && /*#__PURE__*/ React.createElement(Dock, null, lastStepState === "error" && /*#__PURE__*/ React.createElement(Button, {
        size: "lg",
        variant: "outline",
        onClick: ()=>handleUploadAndDownload(styleGroupId)
    }, t("generic.retry")), /*#__PURE__*/ React.createElement(Button, {
        size: "lg",
        onClick: ()=>decrementActiveStep(0)
    }, t("generic.done"))));
}
